import { zodTransformFirestoreTimestampToDate } from "../utils/zodUtils.mjs";
import { zodFlavorId } from "./ids.mjs";
import { defineFirestoreCollection } from "./firebaseCollectionSchema.mjs";
import { PiiCategory, meta } from "./meta.mjs";
import { z as z$1 } from "zod";

// src/models/executionTrace.ts
const firestoreDate = z$1.any().transform(zodTransformFirestoreTimestampToDate);
/** Trace fields carried across an asynchronous boundary. */
const executionTraceSchema = z$1.object({
	runId: zodFlavorId(),
	eventId: zodFlavorId(),
	parentEventId: zodFlavorId().optional()
});
/** Firestore handoff stamp; a writer may know the run before a current event id exists. */
const executionTraceStampSchema = z$1.object({
	runId: zodFlavorId(),
	eventId: zodFlavorId().optional()
});
/** Trace input accepted by emitters; the emitted hop owns its event id. */
const executionTraceEmitSchema = z$1.object({
	runId: zodFlavorId(),
	parentEventId: zodFlavorId().optional()
});
/** Consume-time transport metadata, supplied explicitly by ingress adapters. */
const executionProcessorSchema = z$1.object({
	kind: z$1.enum([
		"pubsub_push",
		"webhook",
		"firestore_trigger",
		"callable",
		"schedule",
		"task"
	]).optional(),
	functionName: z$1.string().min(1).optional(),
	service: z$1.string().min(1).optional(),
	revision: z$1.string().min(1).optional(),
	region: z$1.string().min(1).optional(),
	projectId: z$1.string().min(1).optional(),
	routerName: z$1.string().min(1).optional(),
	messageId: z$1.string().min(1).optional(),
	subscription: z$1.string().min(1).optional(),
	executionId: z$1.string().min(1).optional(),
	/** @deprecated In-flight compatibility; new writers use functionName. */
	name: z$1.string().min(1).optional(),
	/** @deprecated In-flight compatibility; new writers use kind. */
	transport: z$1.enum([
		"http",
		"eventarc",
		"pubsub",
		"callable",
		"schedule",
		"task",
		"firestore"
	]).optional()
});
const executionRunOriginSchema = z$1.object({ kind: z$1.string().min(1) });
/** Immutable root row for one end-to-end execution. */
const executionRunSchema = z$1.object({
	id: zodFlavorId(),
	origin: executionRunOriginSchema,
	eventSource: z$1.string().min(1),
	unexpectedMint: z$1.boolean().optional(),
	createdAt: firestoreDate
});
/** Immutable global event envelope for one consumed hop. */
const executionEventSchema = z$1.object({
	id: zodFlavorId(),
	runId: zodFlavorId(),
	parentEventId: zodFlavorId().optional(),
	eventLocation: z$1.string().min(1),
	eventType: z$1.string().min(1),
	eventSource: z$1.string().min(1),
	eventTime: firestoreDate,
	syncId: z$1.string().min(1).optional(),
	detail: meta(z$1.record(z$1.string(), z$1.unknown()).optional(), {
		bqType: "stringified_map",
		pii: { category: "freeText" }
	}),
	processor: executionProcessorSchema.optional(),
	ingress: meta(z$1.record(z$1.string(), z$1.unknown()).optional(), {
		bqType: "stringified_map",
		pii: { category: "freeText" }
	}),
	createdAt: firestoreDate
});
const ExecutionRuns = defineFirestoreCollection("execution_runs", executionRunSchema, void 0, {
	dwReplication: true,
	firestoreCollectionPath: "execution_runs"
});
const Events = defineFirestoreCollection("events", executionEventSchema, void 0, {
	dwReplication: true,
	firestoreCollectionPath: "events"
});

export { Events, ExecutionRuns, executionEventSchema, executionProcessorSchema, executionRunOriginSchema, executionRunSchema, executionTraceEmitSchema, executionTraceSchema, executionTraceStampSchema };
//# sourceMappingURL=executionTrace.mjs.map